import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Text, Button, SkeletonLoader, Accordion } from '@msc-b2c/shared-components';
import styles from './VoyagersClubMembershipPointsRecap.module.scss';
import {
  getYearFromIsoDate,
  managePlurals,
  replacePlaceholders,
  resolveDateFormat,
  scrollToComponent,
  useShipAndPortNameMaps,
} from '@msc-b2c/shared-utils';
import { VoyagerClubTeaser } from '../VoyagersClubTeaser/VoyagerClubTeaser';
import { VoyagerClubMembershipPointsRecapProps } from '@msc-b2c/interfaces/types';
import dayjs from 'dayjs';

export function VoyagersClubMembershipPointsRecap({
  // Generate a unique ID for the select element for accessibility
  // Determine if there are cruises to show in the list
  // Number of cruise items to show by default and increment on 'Load More'
  // State to track how many cruise items are currently visible
  // Caches for resolved ship and port names to avoid redundant lookups
  pointTitle,
  noPointsTitle,
  noPointsDescription,
  filterPointsTitle,
  durationDaysTitle,
  durationNightsTitle,
  cruiseInformationsLabel,
  durationLabel,
  pointsLabel,
  durationValue,
  departureLabel,
  arrivalLabel,
  pointDetailsLabel,
  experienceLabel,
  formatPointValue,
  nightLabel,
  onBoardPurchasesLabel,
  onBoardServiceLabel,
  loadMoreButtonLabel,
  showLessButtonLabel,
  viewCruisesLabel,
  noPointsButtonUrl,
  cruises,
  pointsAllValue,
}: Readonly<VoyagerClubMembershipPointsRecapProps>) {
  const selectId = useId();
  const componentRef = useRef<HTMLDivElement>(null);
  const showListCruises = cruises && cruises.length > 0;
  const elementToShow = 3;
  const [visibleCount, setVisibleCount] = useState(elementToShow);
  const [shipNameByCodeCache, setShipNameByCodeCache] = useState<Record<string, string>>({});
  const [portNameByCodeCache, setPortNameByCodeCache] = useState<Record<string, string>>({});

  const SHIP_CODE_REMAP: Readonly<Record<string, string>> = {
    AR: 'AX',
    LI: 'LX',
    MX: 'MA',
    OP: 'OX',
    SI: 'SX',
  };

  function extractShipCodeFromVoyagerCruiseId(shipNameOrCruiseId?: string): string | null {
    // Extracts the ship code from a cruise ID or ship name string
    if (!shipNameOrCruiseId) return null;
    const value = shipNameOrCruiseId.trim().toUpperCase();

    let code: string | null = null;
    if (/^[A-Z]{2}$/.test(value)) {
      code = value;
    } else {
      const match = RegExp(/^([A-Z]{2}).*/).exec(value);
      code = match?.[1] ?? null;
    }

    return code ? SHIP_CODE_REMAP[code] ?? code : null;
  }

  function normalizePortCode(port?: string): string | null {
    // Normalizes a port code to a 3-letter uppercase string if valid
    if (!port) return null;
    const value = port.trim().toUpperCase();
    if (/^[A-Z]{3}$/.test(value)) return value;
    return null;
  }

  const items = useMemo(
    // Map cruise data to include additional labels and IDs for rendering
    () =>
      (cruises ?? [])
        .map((item, index) => ({
          id: index + 1,
          totalPointsLabel: replacePlaceholders(managePlurals(0, pointsLabel), {
            numberOfPoints: String(item.totalPoints ?? 0),
          }),
          ...item,
        }))
        .sort((a, b) => {
          // Sort by most recent departure date first (descending order)
          const dateA = new Date(a.departureDate ?? '').getTime();
          const dateB = new Date(b.departureDate ?? '').getTime();
          return dateB - dateA;
        }),
    [cruises, pointsLabel]
  );
  const years = useMemo(() => {
    // Compute unique years from cruise departure dates for filtering
    const uniq = Array.from(new Set(items.map((i) => getYearFromIsoDate(i.departureDate ?? ''))))
      .filter(Boolean)
      .sort((a, b) => b - a);
    return [pointsAllValue, ...uniq.map(String)] as const;
  }, [items, pointsAllValue]);

  const [filterYear, setFilterYear] = useState<(typeof years)[number]>(pointsAllValue);
  // State to track the selected year filter
  const [openId] = useState<number | null>(null);
  // State to track which cruise item panel is open

  const filtered = useMemo(() => {
    // Filter cruise items by selected year, or show all if 'All Points' is selected
    if (filterYear === pointsAllValue) return items;
    const y = Number(filterYear);
    return items.filter((i) => i.departureDate && getYearFromIsoDate(i.departureDate) === y);
  }, [items, filterYear, pointsAllValue]);

  // Show only first N by default, increment by N on 'Load More', reset on 'Show Less'
  const visibleItems = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);
  // Only show the first N filtered cruise items, incrementally as user loads more

  // Resolve ship/port names only for the cruises currently shown (incremental as the list grows)

  // Only resolve codes not already cached

  const shipCodesToResolve = useMemo(() => {
    // Find ship codes for currently visible cruises that are not yet cached
    const codes = visibleItems
      .map((c) => extractShipCodeFromVoyagerCruiseId(c.shipName))
      .filter((c): c is string => Boolean(c));
    // Remove duplicates and already cached
    return Array.from(new Set(codes.filter((code) => !(code in shipNameByCodeCache))));
  }, [visibleItems, shipNameByCodeCache]);

  const portCodesToResolve = useMemo(() => {
    // Find port codes for currently visible cruises that are not yet cached
    const codes = visibleItems
      .flatMap((c) => [normalizePortCode(c.departurePort), normalizePortCode(c.arrivalPort)])
      .filter((c): c is string => Boolean(c));
    // Remove duplicates and already cached
    return Array.from(new Set(codes.filter((code) => !(code in portNameByCodeCache))));
  }, [visibleItems, portNameByCodeCache]);

  const { shipNameByCode, portNameByCode, isLoading } = useShipAndPortNameMaps({
    // Custom hook to resolve ship and port names for codes not yet cached
    shipCodes: shipCodesToResolve,
    portCodes: portCodesToResolve,
  });

  useEffect(() => {
    // Update caches with newly resolved ship and port names
    // Cache resolved names to avoid losing them when the resolver updates (e.g. when loading more cruises)
    setShipNameByCodeCache((prev) => ({ ...prev, ...shipNameByCode }));
    setPortNameByCodeCache((prev) => ({ ...prev, ...portNameByCode }));
  }, [shipNameByCode, portNameByCode]);

  function formatDuration(
    // Format the duration of a cruise using provided templates for days and nights
    days: number,
    nights: number,
    daysTemplate: string,
    nightsTemplate: string,
    orderTemplate: string
  ) {
    const dayText = replacePlaceholders(managePlurals(days, daysTemplate), {
      numberOfDays: String(days),
    }).trim();
    const nightText = replacePlaceholders(managePlurals(nights, nightsTemplate), {
      numberOfNights: String(nights),
    }).trim();
    return replacePlaceholders(orderTemplate, { day: dayText, night: nightText });
  }

  function formatPointsValueFormatting(points: number, template: string) {
    // Format the points value using a template and pluralization
    return replacePlaceholders(managePlurals(points, template), {
      numberOfPoints: points + '',
    });
  }

  function safeSum(...args: Array<number | null | undefined>): number {
    // Safely sum numbers, treating null/undefined as zero
    return args.reduce((acc: number, val) => acc + (val ?? 0), 0);
  }

  function renderKV(k: string | undefined, v: string | React.ReactNode | undefined) {
    // Render a key-value pair for cruise details
    return (
      <div className={styles['cmp-points-recap__kv']}>
        <Text
          text={k ?? ''}
          tagHtml="span"
          styleOption={`${styles['cmp-points-recap__kv-key']}`}
        />
        <div className={`${styles['cmp-points-recap__kv-value']}`}>
          {typeof v === 'string' ? (
            <Text
              text={v}
              tagHtml="span"
            />
          ) : (
            v
          )}
        </div>
      </div>
    );
  }

  function handleLoadMore() {
    // Show all cruise items when 'Load More' is clicked
    setVisibleCount(filtered.length);
  }

  function handleShowLess() {
    setVisibleCount(elementToShow);
    if (componentRef.current) {
      scrollToComponent(componentRef.current);
    }
  }

  return (
    <div
      className={styles['cmp-points-recap']}
      ref={componentRef}
    >
      <div
        className={`${styles['cmp-points-recap__header']} ${styles['cmp-points-recap__border-bottom']}`}
      >
        <Text
          text={pointTitle}
          tagHtml="h2"
          styleOption={`${styles['cmp-points-recap__title']} cmp-text`}
        />
        <div className={`${styles['cmp-points-recap__filter']}`}>
          <Text
            text={filterPointsTitle}
            tagHtml="span"
            styleOption={`${styles['cmp-points-recap__filter-title']} `}
          />
          <select
            id={selectId}
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            aria-label={filterPointsTitle}
            className={styles['cmp-points-recap__filter-select']}
            disabled={!showListCruises || !years?.length}
          >
            {years.map((y, index) => (
              <option
                key={`${String(y ?? 'all-points')}-${index}`}
                value={y}
              >
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>
      {showListCruises && filtered.length > 0 ? (
        <div
          className={styles['cmp-points-recap__list']}
          role="list"
        >
          {visibleItems.map((item) => {
            const isOpen = item.id === openId;
            const days = safeSum(item.durationInNights, 1);
            const shipCode = extractShipCodeFromVoyagerCruiseId(item.shipName);
            const shouldShowShipSkeleton = Boolean(
              isLoading && shipCode && !shipNameByCodeCache[shipCode]
            );
            const displayShipName = shipCode
              ? shipNameByCodeCache[shipCode] ?? shipCode
              : item.shipName;
            const depCode = normalizePortCode(item.departurePort);
            const arrCode = normalizePortCode(item.arrivalPort);
            const shouldShowDepartureSkeleton = Boolean(
              isLoading && depCode && !portNameByCodeCache[depCode]
            );
            const shouldShowArrivalSkeleton = Boolean(
              isLoading && arrCode && !portNameByCodeCache[arrCode]
            );
            const displayDeparturePort = depCode
              ? portNameByCode[depCode] ?? depCode
              : item.departurePort;
            const displayArrivalPort = arrCode
              ? portNameByCode[arrCode] ?? arrCode
              : item.arrivalPort;
            return (
              <div
                className={[
                  styles['cmp-points-recap__item'],
                  isOpen ? styles['cmp-points-recap__item--open'] : '',
                ].join(' ')}
                key={item.id}
                role="listitem"
              >
                {(shouldShowShipSkeleton && (
                  <div className={styles['cmp-points-recap__row']}>
                    <div className={styles['cmp-points-recap__info']}>
                      <SkeletonLoader
                        width="180px"
                        height="18px"
                        styleOption={styles['cmp-points-recap__item-title']}
                      />
                    </div>
                  </div>
                )) || (
                  <Accordion
                    key={item.id}
                    question={displayShipName ?? 'no text'}
                    openingIcon="down"
                    closingIcon="up"
                    multi={true}
                    answer=""
                    isOpen={false}
                    onToggle={() => console.log('toggled')}
                    subtitle={
                      item?.departureDate
                        ? dayjs(item.departureDate).format(resolveDateFormat())
                        : ''
                    }
                    iconLabelNoUnderline={`${item?.totalPoints ? item?.totalPointsLabel : ''}`}
                  >
                    <div
                      id={`panel-${item.id}`}
                      className={styles['cmp-points-recap__panel']}
                      data-open={true}
                    >
                      <div className={styles['cmp-points-recap__panel-inner']}>
                        <div className={styles['cmp-points-recap__panel-grid']}>
                          <div>
                            <Text
                              text={cruiseInformationsLabel}
                              tagHtml="div"
                              styleOption={`cmp-text__normal ${styles['cmp-points-recap__panel-title']} `}
                            />
                            {renderKV(
                              `${durationLabel ?? ''}`,
                              formatDuration(
                                days,
                                item.durationInNights ?? 0,
                                durationDaysTitle ?? '',
                                durationNightsTitle ?? '',
                                durationValue ?? ''
                              )
                            )}
                            {renderKV(
                              departureLabel,
                              shouldShowDepartureSkeleton ? (
                                <SkeletonLoader
                                  width="140px"
                                  height="14px"
                                />
                              ) : (
                                displayDeparturePort
                              )
                            )}
                            {renderKV(
                              arrivalLabel,
                              shouldShowArrivalSkeleton ? (
                                <SkeletonLoader
                                  width="140px"
                                  height="14px"
                                />
                              ) : (
                                displayArrivalPort
                              )
                            )}
                          </div>
                          <div>
                            {pointDetailsLabel &&
                            (experienceLabel ||
                              onBoardPurchasesLabel ||
                              onBoardServiceLabel ||
                              nightLabel) ? (
                              <Text
                                text={pointDetailsLabel}
                                tagHtml="div"
                                styleOption={`${styles['cmp-points-recap__panel-title']} cmp-text__normal`}
                              />
                            ) : null}
                            {experienceLabel
                              ? renderKV(
                                  experienceLabel,
                                  formatPointsValueFormatting(
                                    item.points?.[0]?.experience ?? 0,
                                    formatPointValue ?? ''
                                  )
                                )
                              : null}
                            {onBoardPurchasesLabel
                              ? renderKV(
                                  onBoardPurchasesLabel,
                                  formatPointsValueFormatting(
                                    item.points?.[0]?.onboard ?? 0,
                                    formatPointValue ?? ''
                                  )
                                )
                              : null}
                            {onBoardServiceLabel
                              ? renderKV(
                                  onBoardServiceLabel,
                                  formatPointsValueFormatting(
                                    item.points?.[0]?.preboard ?? 0,
                                    formatPointValue ?? ''
                                  )
                                )
                              : null}
                            {nightLabel
                              ? renderKV(
                                  nightLabel,
                                  formatPointsValueFormatting(
                                    item.points?.[0]?.night ?? 0,
                                    formatPointValue ?? ''
                                  )
                                )
                              : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Accordion>
                )}
              </div>
            );
          })}
          {/* Load More / Show Less CTA */}
          {filtered.length > visibleCount && (
            <div className={styles['cmp-points-recap__panel-load']}>
              <Button
                styleOption="cmp-points-recap__toggle-button cmp-points-recap__loadmore cmp-text__normal"
                onClick={handleLoadMore}
                text={loadMoreButtonLabel}
              />
            </div>
          )}
          {filtered.length > elementToShow && visibleCount >= filtered.length && (
            <div className={styles['cmp-points-recap__panel-load']}>
              <Button
                styleOption="cmp-points-recap__toggle-button cmp-points-recap__loadmore cmp-text__normal"
                onClick={handleShowLess}
                text={showLessButtonLabel}
              />
            </div>
          )}
        </div>
      ) : (
        <div className={styles['cmp-points-recap__no-cruises-container']}>
          <VoyagerClubTeaser
            bannerTitle={noPointsTitle}
            bannerSubtitle={noPointsDescription}
            bannerButtonLabel={viewCruisesLabel}
            bannerButtonUrl={noPointsButtonUrl}
            noHorizzontalPadding={true}
            noBorder={true}
          />
        </div>
      )}
    </div>
  );
}

export default VoyagersClubMembershipPointsRecap;
